import React, { FC, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { Button, Spinner } from 'flowbite-react';

import { CardContainer } from '../../layout';
import { DeviceIpInfo, DeviceLogs } from '../../components';
import { DeviceInformationCard } from './components/DeviceInformationCard';
import { DeviceInformationForm } from './components/DeviceInformationForm';
import { ProfileCard } from './components/ProfileCard';
import { AgentProfileCard } from './components/AgentProfileCard';
import { DeviceMessages } from './components/DeviceMessages';
import { TestMessageForm } from './components/TestMessageForm';
import { TelegramLoginForm } from './components/TelegramLoginForm';
import { useGlobal } from '../../hooks/useGlobal';
import { useWebSocket } from '../../hooks/useSocket';
import { canView } from '../../utils/permissions';
import { getDeviceById, getDeviceInfo } from '../../services';
import { DeviceType, IDevice, WAClientStatus } from '../../types';
import { BonusRequests } from './components/BonusRequests';

// WhatsApp device types that support logs and IP info
const WHATSAPP_DEVICE_TYPES: DeviceType[] = [
  DeviceType.INVITE,
  DeviceType.BOT,
  DeviceType.AGENT_WP,
];

export const DeviceDetailPage: FC = () => {
  const { user } = useGlobal();
  const { latestMessage } = useWebSocket();
  const { id } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [deviceDetail, setDeviceDetail] = useState<IDevice>();
  const [deviceInfo, setDeviceInfo] = useState<any>(null);

  const [qr, setQr] = useState<string | null>(null);
  const [deviceInfoLoading, setDeviceInfoLoading] = useState<boolean>(true);
  const [qrLoading, setQrLoading] = useState<boolean>(false);

  // get page actions from user permissions
  const pageAction = useMemo(() => {
    return user?.permissions.pages.deviceDetails.actions;
  }, [user]);

  // Redirect to home if user has no permission to view device details page
  if (!canView(user, 'deviceDetails')) {
    return <Navigate to="/" />;
  }

  // fetch device detail
  const fetchData = async (id: string) => {
    try {
      const res: any = await getDeviceById(id);

      const { data } = res;

      // set device data
      setDeviceDetail(data);

      // set qr
      setQr(data?.remote?.qr || null);
    } catch (error) {
      console.error('fetchDeviceDetail: ', error);
    } finally {
      setLoading(false);
      setQrLoading(false);
    }
  };

  // fetch device info
  const fetchDeviceInfo = async (id: string) => {
    try {
      const res: any = await getDeviceInfo(id);
      setDeviceInfo(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setDeviceInfoLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id]);

  useEffect(() => {
    if (!deviceDetail?._id) {
      setDeviceInfoLoading(false);
      return;
    }

    const { deviceType, _id: id } = deviceDetail;

    const allowedTypes: DeviceType[] = [
      DeviceType.INVITE,
      DeviceType.BOT,
      DeviceType.AGENT_WP,
    ];

    if (allowedTypes.includes(deviceType)) {
      fetchDeviceInfo(id);
    } else {
      setDeviceInfoLoading(false);
    }
  }, [deviceDetail]);

  useEffect(() => {
    const defaultTitle = 'WP Invite Panel';
    const updatedTitle = `${deviceDetail?._id ?? ''} | ${defaultTitle}`;
    document.title = updatedTitle;

    if (deviceDetail?.clientStatus === 'CONNECTED') {
      setFavicon('/favicon-online.ico?v=1');
    } else {
      setFavicon('/favicon-offline.ico?v=2');
    }

    return () => {
      document.title = defaultTitle;
      setFavicon('/favicon.ico?v=3');
    };
  }, [deviceDetail]);

  useEffect(() => {
    if (latestMessage && deviceDetail) {
      const { _id: id, clientStatus } = deviceDetail;
      const { eventType, data } = latestMessage as any;

      if (data?.clientId === id) {
        if (eventType === 'QR' && clientStatus === WAClientStatus.UNPAIRED) {
          setQr(data.qr);
        } else {
          fetchData(id);
        }
      }
    }
  }, [latestMessage, deviceDetail]);

  const setFavicon = (href: string) => {
    let link: HTMLLinkElement | null =
      document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
  };

  return (
    <>
      {loading ? (
        <div className="text-center py-20">
          <Spinner aria-label="device detail loading" size="xl" />
        </div>
      ) : (
        <div className="flex gap-4">
          {/* left */}
          <div className="w-[400px] flex-none">
            <div className="flex flex-col gap-4">
              {/* profile info */}
              {deviceDetail?.clientStatus == WAClientStatus.CONNECTED &&
                deviceDetail?.deviceType === DeviceType.BOT && (
                  <ProfileCard
                    deviceInfo={deviceInfo}
                    loading={deviceInfoLoading}
                  />
                )}

              {deviceDetail?.clientStatus == WAClientStatus.CONNECTED &&
                deviceDetail?.deviceType === DeviceType.AGENT_WP && (
                  <AgentProfileCard
                    deviceInfo={deviceInfo}
                    loading={deviceInfoLoading}
                    deviceDetail={deviceDetail}
                    pageAction={pageAction}
                    onUpdate={() => {
                      location.reload();
                    }}
                  />
                )}

              {/* device status */}
              <DeviceInformationCard deviceDetail={deviceDetail} />

              {/* qr area */}
              {WHATSAPP_DEVICE_TYPES.includes(
                deviceDetail?.deviceType as DeviceType,
              ) &&
                deviceDetail?.clientStatus === WAClientStatus.UNPAIRED &&
                pageAction?.showQr && (
                  <CardContainer
                    title="QR"
                    action={() => {
                      return (
                        <Button
                          size="xs"
                          color="blue"
                          isProcessing={qrLoading}
                          disabled={qrLoading}
                          onClick={() => {
                            setQrLoading(true);
                            fetchData(deviceDetail?._id);
                          }}
                        >
                          Yenile
                        </Button>
                      );
                    }}
                  >
                    <QRCode
                      value={qr || ''}
                      size={250}
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        height: 'auto',
                      }}
                    />
                  </CardContainer>
                )}

              {/* telegram login */}
              {deviceDetail?.deviceType === DeviceType.AGENT_TG &&
                deviceDetail?.clientStatus === WAClientStatus.UNPAIRED &&
                pageAction?.showQr && (
                  <TelegramLoginForm
                    deviceId={deviceDetail?._id || ''}
                    onSuccess={() => {
                      if (deviceDetail._id) {
                        fetchData(deviceDetail._id);
                      }
                    }}
                  />
                )}

              {/* send message */}
              {deviceDetail?.clientStatus === WAClientStatus.CONNECTED &&
                deviceDetail?.deviceType === DeviceType.INVITE &&
                pageAction?.testMessage && (
                  <TestMessageForm deviceId={deviceDetail?._id || ''} />
                )}
            </div>
          </div>
          {/* right */}
          <div className="w-full gap-4 flex flex-col">
            <CardContainer title="Cihaz Bilgileri">
              <DeviceInformationForm
                deviceDetail={deviceDetail}
                pageAction={pageAction}
                onUpdate={() => console.log('update')}
              />
            </CardContainer>
            {deviceDetail?.deviceType === DeviceType.BOT && (
              <CardContainer title="Bonus İstekleri">
                <BonusRequests id={deviceDetail?._id || ''} />
              </CardContainer>
            )}
            {deviceDetail?.deviceType === DeviceType.INVITE && (
              <CardContainer title="Gönderilen Mesajlar">
                <DeviceMessages id={deviceDetail?._id || ''} />
              </CardContainer>
            )}
            {WHATSAPP_DEVICE_TYPES.includes(
              deviceDetail?.deviceType as DeviceType,
            ) && (
              <>
                <CardContainer title="Logs">
                  <DeviceLogs id={deviceDetail?._id || ''} />
                </CardContainer>
                <CardContainer title="IP Bilgileri">
                  <DeviceIpInfo
                    deviceInfo={deviceInfo}
                    loading={deviceInfoLoading}
                  />
                </CardContainer>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
